/**
 * Contrato do **lote de associação de perks** — espelha
 * `src/_shared/domain/perk-association-batch.ts` do monolith. Os três alvos do
 * editor (produto, preset e usuário) mandam o diff inteiro e recebem o mesmo
 * formato de erro, então quem trata o 422 é o editor, não cada alvo.
 *
 * Ver `primum-core-root/docs/plans/perk-association-editor-plan.md` §3 e §4.
 */

/**
 * Teto de itens por array (`add` e `remove` contam separado), validado por
 * `@ArrayMaxSize` nas DTOs do back. Aqui serve para barrar antes do round-trip:
 * estourar o limite vira 400 genérico, sem lista de erros por índice.
 */
export const MAX_PERK_ASSOCIATION_BATCH = 50;

/** Em qual metade do diff o item problemático estava. */
export type PerkAssociationBatchOperation = 'add' | 'remove';

/**
 * No alvo usuário os arrays se chamam `grant`/`revoke`, mas o back continua
 * respondendo `'add'`/`'remove'` — o `index` é a posição em `grant[]`/`revoke[]`.
 */
export type PerkAssociationBatchErrorCode =
  | 'PERK_NOT_FOUND'
  | 'ALREADY_ASSOCIATED'
  | 'DUPLICATE_IN_BATCH'
  | 'INVALID_VALUES'
  | 'ASSOCIATION_NOT_FOUND'
  | 'PERK_SCOPE_MISMATCH';

export interface PerkAssociationBatchError {
  operation: PerkAssociationBatchOperation;
  /** Índice dentro do array `add` ou `remove` do corpo da request. */
  index: number;
  code: PerkAssociationBatchErrorCode;
  message: string;
}

/**
 * O `message` do back é em inglês e cita ids. Estes rótulos são o que o operador
 * lê no card; a mensagem crua fica como detalhe secundário.
 */
export const PERK_ASSOCIATION_BATCH_ERROR_LABELS: Record<
  PerkAssociationBatchErrorCode,
  string
> = {
  PERK_NOT_FOUND: 'Este perk não existe mais no catálogo.',
  ALREADY_ASSOCIATED: 'Este perk já está associado ao alvo.',
  DUPLICATE_IN_BATCH: 'Este perk aparece mais de uma vez no lote.',
  INVALID_VALUES: 'Os valores não batem com a política de concessão do perk.',
  ASSOCIATION_NOT_FOUND: 'Esta associação não existe mais.',
  PERK_SCOPE_MISMATCH: 'Este perk não vale na plataforma do perfil.',
};

/** `error` do corpo do 422 lançado por `PerkAssociationBatchInvalidException`. */
const BATCH_INVALID_ERROR = 'PERK_ASSOCIATION_BATCH_INVALID';

const OPERATIONS: PerkAssociationBatchOperation[] = ['add', 'remove'];

const CODES: PerkAssociationBatchErrorCode[] = [
  'PERK_NOT_FOUND',
  'ALREADY_ASSOCIATED',
  'DUPLICATE_IN_BATCH',
  'INVALID_VALUES',
  'ASSOCIATION_NOT_FOUND',
  'PERK_SCOPE_MISMATCH',
];

function isBatchError(value: unknown): value is PerkAssociationBatchError {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    OPERATIONS.includes(candidate.operation as PerkAssociationBatchOperation) &&
    typeof candidate.index === 'number' &&
    CODES.includes(candidate.code as PerkAssociationBatchErrorCode) &&
    typeof candidate.message === 'string'
  );
}

/**
 * Extrai a lista de erros por índice de um erro do axios. Devolve `null` quando
 * não é um 422 de lote — aí a falha é genérica (perfil inexistente, 403 de
 * `grantableByPerkKeys`, rede) e a UI mostra só a mensagem.
 */
export function perkAssociationBatchErrors(
  error: unknown,
): PerkAssociationBatchError[] | null {
  const response = (error as { response?: { data?: unknown } } | null)
    ?.response;
  const data = response?.data as
    | { error?: unknown; details?: unknown }
    | undefined;
  if (!data || data.error !== BATCH_INVALID_ERROR) return null;
  if (!Array.isArray(data.details)) return null;

  const details = data.details.filter(isBatchError);
  return details.length > 0 ? details : null;
}
